import {
    applyElementFormatting,
    EMPTY_FORMATTING,
    getFormattingKey,
} from "authoring/routes/project/schedule-editor/lib/formatting-state.mjs"

// The word diff treats tags as separate tokens and only highlights words, so making a word bold
// changes tags alone and shows no highlight. Prefixing every formatted word with a hidden code for
// its formatting makes bold "pump" a different token from plain "pump", so the diff highlights it
// like any other changed word. stripFormattedWordMarkers removes the codes from the finished diff.
// List items get the same treatment, so text turned into a numbered or bulleted list, or moved to
// another level, is highlighted too, as the change detection's markListStructure counts it as a change.
//
// The code is a short hash in private-use characters: the tokenizer splits on punctuation, which a
// readable key (a link address, say) would contain.
const MARKER_START = ""
const MARKER_END = ""
const MARKER_PATTERN = /[0-9a-z]*/g

export function markFormattedWords(html) {
    if (!html || typeof document === "undefined") return html

    const container = document.createElement("div")
    container.innerHTML = html

    for (const textNode of collectTextNodes(container)) {
        const key = [getFormattingKey(getFormattingFor(textNode, container)), getListType(textNode, container)]
            .filter(Boolean)
            .join("+")
        if (!key) continue
        const marker = `${MARKER_START}${hashKey(key)}${MARKER_END}`
        // Split punctuation the same way the diff tokenizer does, so every resulting token is marked
        textNode.nodeValue = textNode.nodeValue
            .replace(/([!?.:;,])(?=[^\s])/g, "$1 ")
            .replace(/\S+/g, (word) => `${marker}${word}`)
    }

    return container.innerHTML
}

export function stripFormattedWordMarkers(html) {
    if (!html) return html
    return html.replace(MARKER_PATTERN, "")
}

function collectTextNodes(container) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
    const nodes = []
    while (walker.nextNode()) nodes.push(walker.currentNode)
    return nodes
}

function getFormattingFor(textNode, container) {
    const ancestors = []
    for (let element = textNode.parentElement; element && element !== container; element = element.parentElement) {
        ancestors.unshift(element)
    }
    return ancestors.reduce(
        (state, element) => applyElementFormatting(state, element.tagName, (name) => element.getAttribute(name)),
        EMPTY_FORMATTING
    )
}

// The nearest list's type and how deeply it is nested, so moving an item underneath another counts
function getListType(textNode, container) {
    const lists = []
    for (let element = textNode.parentElement; element && element !== container; element = element.parentElement) {
        if (element.tagName === "OL" || element.tagName === "UL") lists.push(element)
    }
    if (!lists.length) return ""
    return `list=${lists[0].tagName.toLowerCase()}${lists.length}`
}

function hashKey(key) {
    let hash = 5381
    for (const character of key) {
        hash = ((hash * 33) ^ character.codePointAt(0)) >>> 0
    }
    return hash.toString(36)
}
